import { formatPhoneNumber } from "utils/common";

/**
 * Shape handed to `PatientForm`'s `newPatient` prop. Deliberately narrow: the search
 * bar only ever knows a name, a phone or an email.
 */
export type NewPatientSeed = {
  first_name: string;
  middle_name: string;
  last_name: string;
  phone: string;
  email: string;
};

export const EMPTY_NEW_PATIENT_SEED: NewPatientSeed = {
  first_name: "",
  middle_name: "",
  last_name: "",
  phone: "",
  email: ""
};

/** Sentinel id of the synthetic "Add a new patient" row, matching `PatientSearchOption`. */
export const NEW_PATIENT_OPTION_ID = -1;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_CHARACTERS_PATTERN = /^[+\d\s().-]+$/;

/**
 * `PhoneField` strips everything but digits on change, so the seed has to be digits
 * only — a formatted value would show up masked in the form and fail its validation.
 */
export const toPhoneDigits = (value: string): string => {
  if (!PHONE_CHARACTERS_PATTERN.test(value)) {
    return "";
  }
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) {
    return digits;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return "";
};

/** Uppercases the first letter only — lowercasing the rest would mangle "McDonald". */
const capitalize = (word: string): string =>
  word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word;

const seedFromNameParts = (parts: string[]): NewPatientSeed => {
  const [first = "", ...rest] = parts.map(capitalize);
  return {
    ...EMPTY_NEW_PATIENT_SEED,
    first_name: first,
    middle_name: rest.length > 1 ? rest.slice(0, -1).join(" ") : "",
    last_name: rest.length > 0 ? rest[rest.length - 1] : ""
  };
};

/**
 * Turns whatever the user typed into the search bar into a prefill for the new patient
 * form. Anything we cannot confidently read (a patient id, a serial number, a single
 * digit) yields an empty seed rather than a garbage first name.
 */
export function buildNewPatientSeed(searchValue: string): NewPatientSeed {
  const value = searchValue.trim().replace(/\s+/g, " ");
  if (!value) {
    return EMPTY_NEW_PATIENT_SEED;
  }

  if (EMAIL_PATTERN.test(value)) {
    return { ...EMPTY_NEW_PATIENT_SEED, email: value };
  }

  const phone = toPhoneDigits(value);
  if (phone) {
    return { ...EMPTY_NEW_PATIENT_SEED, phone };
  }

  // Numeric-only input we could not read as a phone number is an id or a serial —
  // there is nothing to prefill from it.
  if (!/[a-zA-Z]/.test(value)) {
    return EMPTY_NEW_PATIENT_SEED;
  }

  // "Doe, John A" — the comma decides which side is the surname.
  if (value.includes(",")) {
    const [surname = "", ...rest] = value
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const givenNames = rest.join(" ").split(" ").filter(Boolean);
    return {
      ...EMPTY_NEW_PATIENT_SEED,
      first_name: capitalize(givenNames[0] ?? ""),
      middle_name: givenNames.slice(1).map(capitalize).join(" "),
      last_name: capitalize(surname)
    };
  }

  return seedFromNameParts(value.split(" ").filter(Boolean));
}

export const getSeedFullName = (seed: NewPatientSeed): string =>
  [seed.first_name, seed.middle_name, seed.last_name].filter(Boolean).join(" ");

/**
 * Copy for the dropdown row. Says exactly what will be carried into the form so the
 * action never looks like it discards what was typed.
 */
export function describeNewPatientSeed(seed: NewPatientSeed): {
  primary: string;
  secondary: string;
} {
  const fullName = getSeedFullName(seed);
  if (fullName) {
    return {
      primary: `Add "${fullName}" as a new patient`,
      secondary: "Opens the quick add form with the name filled in"
    };
  }
  if (seed.phone) {
    return {
      primary: "Add a new patient",
      secondary: `Opens the quick add form with phone ${
        formatPhoneNumber(seed.phone, "parenthesis") ?? seed.phone
      } filled in`
    };
  }
  if (seed.email) {
    return {
      primary: "Add a new patient",
      secondary: `Opens the quick add form with email ${seed.email} filled in`
    };
  }
  return {
    primary: "Add a new patient",
    secondary: "Opens the quick add form"
  };
}
